import {
  PRACTICE_RETRY_MS,
  type PracticeStreak,
  calendarDay,
  deviceTimezone,
  effectiveStreak,
} from "./streak";

type Activity = { occurredAt: string; timezone: string; source: "web" | "mobile" };
type Reply = { streak: PracticeStreak; hitMilestone?: boolean; milestoneDays?: number };
export type PracticeState = {
  streak: PracticeStreak | null;
  loading: boolean;
  error: string | null;
};
const empty: PracticeState = { streak: null, loading: false, error: null };
type Adapter = {
  read: (key: string) => Promise<string | null>;
  write: (key: string, value: string) => Promise<void>;
  request: (userId: string, activity?: Activity) => Promise<Reply>;
  source: Activity["source"];
  lock?: (key: string, work: () => Promise<void>) => Promise<void>;
  milestone?: (days: number) => void;
};

/** One small account/day outbox; transport and storage are platform adapters. */
export function createPracticeClient(adapter: Adapter) {
  const states = new Map<string, PracticeState>();
  const listeners = new Map<string, Set<() => void>>();
  const operations = new Map<string, Promise<void>>();
  const refreshes = new Map<string, Promise<void>>();
  const key = (id: string) => `practice-days:v1:${id}`;
  const publish = (id: string, state: PracticeState) => {
    states.set(id, state);
    listeners.get(id)?.forEach((fn) => fn());
  };
  const snapshot = (id: string | null) => (id ? states.get(id) || empty : empty);
  const run = (id: string, activity?: Activity) => {
    const work = async () => {
      const old = snapshot(id);
      try {
        const saved = JSON.parse((await adapter.read(key(id))) || "{}") as {
          pending?: Activity[];
          streak?: PracticeStreak;
        };
        let streak = saved.streak || old.streak || null;
        const all = Array.isArray(saved.pending) ? saved.pending : [];
        const expired = all.some(
          (item) => Date.parse(item.occurredAt) < Date.now() - PRACTICE_RETRY_MS
        );
        const pending = all.filter(
          (item) =>
            Number.isFinite(Date.parse(item.occurredAt)) &&
            Date.parse(item.occurredAt) >= Date.now() - PRACTICE_RETRY_MS
        );
        const zone = streak?.timezone || activity?.timezone || deviceTimezone();
        if (activity) {
          const day = calendarDay(Date.parse(activity.occurredAt), zone);
          if (
            streak?.lastActivityDate !== day &&
            !pending.some((p) => calendarDay(Date.parse(p.occurredAt), zone) === day)
          ) {
            pending.push(activity);
          }
        }
        if (activity && !pending.length && streak && !old.error) {
          publish(id, { streak: effectiveStreak(streak), loading: false, error: null });
          return;
        }
        publish(id, {
          streak: streak ? effectiveStreak(streak) : null,
          loading: true,
          error: null,
        });
        const persist = () => adapter.write(key(id), JSON.stringify({ pending, streak }));
        // Save before transport. Failed attempts keep their original timestamp.
        await persist();
        if (streak) publish(id, { streak: effectiveStreak(streak), loading: true, error: null });
        let recorded = false;
        for (const item of [...pending]) {
          const reply = await adapter.request(id, item);
          streak = reply.streak;
          pending.splice(pending.indexOf(item), 1);
          await persist();
          recorded = true;
          if (reply.hitMilestone && reply.milestoneDays) adapter.milestone?.(reply.milestoneDays);
        }
        if (!recorded) streak = (await adapter.request(id)).streak;
        await persist();
        publish(id, {
          streak,
          loading: false,
          error: expired ? "An older practice update expired before it could sync." : null,
        });
      } catch {
        const current = snapshot(id);
        publish(id, {
          streak: current.streak ? effectiveStreak(current.streak) : null,
          loading: false,
          error:
            "Practice progress could not sync. Showing last known progress; retry when connected.",
        });
      }
    };
    const operation = (operations.get(id) || Promise.resolve()).then(() =>
      adapter.lock ? adapter.lock(key(id), work) : work()
    );
    operations.set(id, operation);
    void operation.finally(() => {
      if (operations.get(id) === operation) operations.delete(id);
    });
    return operation;
  };
  return {
    snapshot,
    subscribe(id: string | null, fn: () => void) {
      if (!id) return () => {};
      const set = listeners.get(id) || new Set();
      set.add(fn);
      listeners.set(id, set);
      return () => {
        set.delete(fn);
      };
    },
    refresh(id: string) {
      const existing = refreshes.get(id);
      if (existing) return existing;
      const promise = run(id);
      refreshes.set(id, promise);
      void promise.finally(() => refreshes.delete(id));
      return promise;
    },
    record(id: string, timestamp = Date.now()) {
      const current = snapshot(id);
      if (
        current.streak?.timezone &&
        !current.error &&
        current.streak.lastActivityDate === calendarDay(timestamp, current.streak.timezone)
      ) {
        return Promise.resolve();
      }
      return run(id, {
        occurredAt: new Date(timestamp).toISOString(),
        timezone: deviceTimezone(),
        source: adapter.source,
      });
    },
  };
}
